// Bypass rules — standalone master-admin screen.
//
// Rules live in one of three types (unknown / client / global) and carry an
// OWNER: either an app-to-app access token or the operator. Ownership is purely
// PROVENANCE — a label for "which token/partner created this" — it does NOT
// gate access: over the partner API every valid token sees and manages every
// rule (the token is auth only). The operator can (re)assign a rule's owner
// here to correct or annotate that label.

import { useEffect, useState } from 'react';
import { I } from '../components/icons';
import { Modal, StatusDot, useToast, Empty } from '../components/ui';
import {
  fetchBypassRules, createBypassRule, transitionBypassRule, deleteBypassRule,
  setBypassRuleOwner, fetchAccessTokens,
  ApiError, type BypassRule, type AccessToken,
} from '../api/client';
import type { AppState } from '../types';

type RuleType = 'unknown' | 'client' | 'global';

const typeBadgeStyle: Record<RuleType, React.CSSProperties> = {
  unknown: { background: 'var(--amber-bg)', color: 'var(--amber)' },
  client:  { background: 'var(--accent-soft)', color: 'var(--accent)' },
  global:  { background: 'var(--violet-bg)', color: 'var(--violet)' },
};

// OwnerCell renders a rule's owner (provenance label): a token chip for the
// token that created/owns it, or a muted "Operator" when there's no owning token.
function OwnerCell({ rule }: { rule: BypassRule }) {
  if (rule.ownerTokenId) {
    return (
      <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            title={`Created by / attributed to access token ${rule.ownerTokenId}`}>
        <I.Key size={10} style={{ marginRight: 4 }}/>{rule.ownerTokenLabel ?? 'token'}
      </span>
    );
  }
  return <span className="muted" style={{ fontSize: 11.5 }}>Operator</span>;
}

export function BypassRulesScreen({ state }: { state: AppState }) {
  const toast = useToast();
  const [rules, setRules] = useState<BypassRule[]>([]);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | RuleType>('all');
  const [ownerFilter, setOwnerFilter] = useState<'all' | 'none' | string>('all'); // 'all' | 'none' | tokenId
  const [q, setQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [transitionFor, setTransitionFor] = useState<BypassRule | null>(null);
  const [assignFor, setAssignFor] = useState<BypassRule | null>(null);
  const [deleteFor, setDeleteFor] = useState<BypassRule | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await fetchBypassRules({
        ...(typeFilter === 'all' ? {} : { type: typeFilter }),
        ...(ownerFilter === 'all' ? {} : { ownerTokenId: ownerFilter }),
      });
      setRules(list);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [typeFilter, ownerFilter]);

  // Tokens power the owner filter + the assign/create selectors. Load once.
  useEffect(() => {
    fetchAccessTokens().then(setTokens).catch(() => setTokens([]));
  }, []);

  const filtered = rules.filter((r) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return r.regex.toLowerCase().includes(needle)
      || (r.clientId ?? '').toLowerCase().includes(needle)
      || (r.ownerTokenLabel ?? '').toLowerCase().includes(needle)
      || r.createdBy.toLowerCase().includes(needle);
  });

  const counts = {
    total: rules.length,
    client:  rules.filter((r) => r.type === 'client').length,
    global:  rules.filter((r) => r.type === 'global').length,
    owned:   rules.filter((r) => !!r.ownerTokenId).length,
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Bypass rules</h1>
          <p className="page-sub">
            Regex rules served by <span className="mono">s3-web-provider</span> — grant public read access to S3 paths.
            Rules are <strong>shared</strong>: over the partner API every access token sees and manages them all.
            The <strong>Owner</strong> is just provenance — which token (or the operator) created the rule.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
          <I.Plus size={13}/> New rule
        </button>
      </div>

      <div className="metric-grid" style={{ marginBottom: 14 }}>
        <div className="metric">
          <div className="metric-label">Total</div>
          <div className="metric-value">{counts.total}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Client</div>
          <div className="metric-value">{counts.client}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Global</div>
          <div className="metric-value">{counts.global}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Token-owned</div>
          <div className="metric-value">{counts.owned}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ gap: 10 }}>
            <div style={{ position: 'relative', width: 240 }}>
              <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
              <input className="input" placeholder="Search regex, client, owner…"
                     style={{ paddingLeft: 30, height: 28, fontSize: 12 }}
                     value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)}>
              <option value="all">All types</option>
              <option value="unknown">unknown</option>
              <option value="client">client</option>
              <option value="global">global</option>
            </select>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
              <option value="all">All owners</option>
              <option value="none">Operator-owned</option>
              {tokens.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            {loading ? 'loading…' : `${filtered.length} shown`}
          </span>
        </div>

        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load: {err}</div>}

        {!loading && filtered.length === 0 && (
          <Empty title="No bypass rules" message="Create one with the button above."/>
        )}

        {filtered.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Type</th>
                <th style={{ width: 130 }}>Client</th>
                <th>Regex</th>
                <th style={{ width: 150 }}>Owner</th>
                <th style={{ width: 54 }}>Mode</th>
                <th style={{ width: 104 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td><span className="badge" style={typeBadgeStyle[r.type]}>{r.type}</span></td>
                  <td><span className="mono" style={{ fontSize: 12 }}>{r.clientId ?? '—'}</span></td>
                  <td><span className="mono" style={{ fontSize: 12, color: 'var(--text-1)' }}>{r.regex}</span></td>
                  <td><OwnerCell rule={r}/></td>
                  <td><span className="badge">{r.accessMode}</span></td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Assign owner token"
                              onClick={() => setAssignFor(r)}>
                        <I.Key size={12}/>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Transition type"
                              onClick={() => setTransitionFor(r)}>
                        <I.ArrowR size={12}/>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Delete"
                              onClick={() => setDeleteFor(r)}>
                        <I.Trash size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CreateRuleModal open={addOpen}
                       onClose={() => setAddOpen(false)}
                       state={state}
                       onCreated={async () => { setAddOpen(false); await reload(); toast?.success('Rule created'); }}/>

      <TransitionRuleModal rule={transitionFor}
                           onClose={() => setTransitionFor(null)}
                           state={state}
                           onDone={async () => { setTransitionFor(null); await reload(); toast?.success('Rule transitioned'); }}/>

      <AssignOwnerModal rule={assignFor}
                        tokens={tokens}
                        onClose={() => setAssignFor(null)}
                        onDone={async () => { setAssignFor(null); await reload(); toast?.success('Owner updated'); }}/>

      <Modal open={deleteFor !== null}
             onClose={() => setDeleteFor(null)}
             title="Delete bypass rule?"
             subtitle="This is permanent."
             footer={
               <>
                 <button className="btn" onClick={() => setDeleteFor(null)}>Cancel</button>
                 <button className="btn btn-danger-solid" onClick={async () => {
                   if (!deleteFor) return;
                   try {
                     await deleteBypassRule(deleteFor.id);
                     setDeleteFor(null);
                     await reload();
                     toast?.success('Rule deleted');
                   } catch (e) {
                     toast?.error('Delete failed', e instanceof ApiError ? e.message : String(e));
                   }
                 }}>
                   <I.Trash size={13}/> Delete
                 </button>
               </>
             }>
        {deleteFor && (
          <div className="mono" style={{ fontSize: 12, padding: 10, background: 'var(--bg-2)', borderRadius: 6 }}>
            {deleteFor.type}: {deleteFor.regex}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Create modal ───────────────────────────────────────────────────────────

function CreateRuleModal({
  open, onClose, state, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  state: AppState;
  onCreated: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [type, setType] = useState<RuleType>('client');
  const [clientId, setClientId] = useState('');
  const [regex, setRegex] = useState('');
  const [service, setService] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setType('client');
      setClientId(state.clients[0]?.id ?? '');
      setRegex('');
      setService('');
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && !clientId) setClientId(state.clients[0]?.id ?? '');
  }, [open, state.clients, clientId]);

  const submit = async () => {
    if (!regex.trim()) return;
    setSubmitting(true);
    try {
      await createBypassRule({
        type,
        clientId: type === 'client' ? clientId : undefined,
        regex: regex.trim(),
        accessMode: 'r',
        service: service.trim() || undefined,
      });
      await onCreated();
    } catch (e) {
      toast?.error('Create failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="New bypass rule"
           subtitle="The backend validator will reject patterns that could leak to other tenants."
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || !regex.trim() || (type === 'client' && !clientId)}
                       onClick={submit}>
                 {submitting ? 'Creating…' : 'Create'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Type</label>
          <select className="select" value={type} onChange={(e) => setType(e.target.value as RuleType)}>
            <option value="client">client — per-tenant, regex strictly bound to tenant prefix</option>
            <option value="global">global — platform-wide path</option>
            <option value="unknown">unknown — imported, not yet classified</option>
          </select>
        </div>

        {type === 'client' && (
          <div className="field">
            <label className="field-label">Tenant</label>
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {state.clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label className="field-label">Regex <span className="req-dot">*</span></label>
          <input className="input input-mono" placeholder={type === 'client'
              ? `^/${clientId || 'tenant-slug'}/public/.*`
              : '^/shared/public/.*'}
                 value={regex} onChange={(e) => setRegex(e.target.value)}/>
          <div className="field-hint">
            Must be anchored (^), contain the tenant prefix verbatim, no top-level alternation, no <span className="mono">..</span> / <span className="mono">//</span>.
          </div>
        </div>

        <div className="field">
          <label className="field-label">Service (optional)</label>
          <input className="input input-mono" placeholder="e.g. comfyui1 — leave empty for all"
                 value={service} onChange={(e) => setService(e.target.value)}/>
        </div>

        <div className="field-hint" style={{ marginTop: 2 }}>
          Bypass rules are shared: every access token can see and manage every rule, so there's no owner
          to pick here. Rules created over the partner API are tagged with the calling token as provenance.
        </div>
      </div>
    </Modal>
  );
}

// ─── Assign owner modal ─────────────────────────────────────────────────────

function AssignOwnerModal({
  rule, tokens, onClose, onDone,
}: {
  rule: BypassRule | null;
  tokens: AccessToken[];
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [ownerTokenId, setOwnerTokenId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (rule) {
      setOwnerTokenId(rule.ownerTokenId ?? '');
      setSubmitting(false);
    }
  }, [rule]);

  if (!rule) return <Modal open={false} onClose={onClose} title=""/>;

  const submit = async () => {
    setSubmitting(true);
    try {
      await setBypassRuleOwner(rule.id, ownerTokenId || null);
      await onDone();
    } catch (e) {
      toast?.error('Reassign failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Show every token (incl. revoked) so a currently-assigned dead token still
  // renders, but flag dead ones.
  const label = (t: AccessToken) => {
    const dead = !!t.revokedAt || (!!t.expiresAt && new Date(t.expiresAt) < new Date());
    return t.label + (dead ? ' (inactive)' : '');
  };

  return (
    <Modal open={true} onClose={onClose}
           title="Assign owner token"
           subtitle={rule.regex}
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || ownerTokenId === (rule.ownerTokenId ?? '')}
                       onClick={submit}>
                 {submitting ? 'Saving…' : 'Save'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Owner</label>
          <select className="select" value={ownerTokenId} onChange={(e) => setOwnerTokenId(e.target.value)}>
            <option value="">Operator-owned (no token)</option>
            {tokens.map((t) => (
              <option key={t.id} value={t.id}>{label(t)}</option>
            ))}
          </select>
          <div className="field-hint">
            Provenance only — re-attributes which token this rule belongs to. It does not change who can
            access or manage it: every token sees and manages every rule.
          </div>
        </div>
        <div className="row" style={{ padding: 10, background: 'var(--bg-2)', borderRadius: 6, fontSize: 12, gap: 8 }}>
          <StatusDot status="healthy"/>
          <span className="mono">{rule.ownerTokenLabel ?? 'Operator'}</span>
          <I.ChevronR size={12}/>
          <span className="mono" style={{ color: 'var(--accent)' }}>
            {ownerTokenId ? (tokens.find((t) => t.id === ownerTokenId)?.label ?? 'token') : 'Operator'}
          </span>
        </div>
      </div>
    </Modal>
  );
}

// ─── Transition modal ───────────────────────────────────────────────────────

function TransitionRuleModal({
  rule, onClose, state, onDone,
}: {
  rule: BypassRule | null;
  onClose: () => void;
  state: AppState;
  onDone: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [newType, setNewType] = useState<RuleType>('client');
  const [clientId, setClientId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (rule) {
      // Default the dropdown to a different type than the current one.
      setNewType(rule.type === 'client' ? 'global' : 'client');
      setClientId(rule.clientId ?? state.clients[0]?.id ?? '');
      setSubmitting(false);
    }
  }, [rule, state.clients]);

  if (!rule) {
    return <Modal open={false} onClose={onClose} title=""/>;
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      await transitionBypassRule(rule.id, {
        type: newType,
        clientId: newType === 'client' ? clientId : undefined,
      });
      await onDone();
    } catch (e) {
      toast?.error('Transition failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose}
           title={`Transition rule (${rule.type} → ?)`}
           subtitle={rule.regex}
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || newType === rule.type || (newType === 'client' && !clientId)}
                       onClick={submit}>
                 {submitting ? 'Working…' : 'Transition'}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">New type</label>
          <select className="select" value={newType} onChange={(e) => setNewType(e.target.value as RuleType)}>
            <option value="unknown">unknown — detach from any tenant</option>
            <option value="client">client — assign to a tenant</option>
            <option value="global">global — promote to platform-wide</option>
          </select>
        </div>
        {newType === 'client' && (
          <div className="field">
            <label className="field-label">Assign to tenant</label>
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {state.clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="field-hint">
              The existing regex will be revalidated against this tenant's prefix.
              If it fails, the transition is rejected.
            </div>
          </div>
        )}
        <div className="row" style={{ padding: 10, background: 'var(--bg-2)', borderRadius: 6, fontSize: 12, gap: 8 }}>
          <StatusDot status="healthy"/>
          <span className="mono">{rule.type}</span>
          <I.ChevronR size={12}/>
          <span className="mono" style={{ color: 'var(--accent)' }}>{newType}</span>
        </div>
      </div>
    </Modal>
  );
}
